'use client';

import { ReactNode } from 'react';

type PanelSize = 'sm' | 'md' | 'lg' | 'xl';

const PANEL_STYLES: Record<PanelSize, string> = {
  sm: 'flex-[3_1_240px]',
  md: 'flex-[4_1_300px]',
  lg: 'flex-[5_1_340px]',
  xl: 'flex-[8_1_400px]',
};

interface ToolLayoutProps {
  children: ReactNode;
  className?: string;
}

export function ToolLayout({ children, className = '' }: ToolLayoutProps) {
  return (
    <div className={`flex flex-wrap items-start gap-4 ${className}`}>
      {children}
    </div>
  );
}

/*
 * Sticky preview/output column. Panels WRAP (flex-wrap on flex-basis), so a caller must pick the
 * breakpoint at which the sum of its panels' bases + gaps fits the content width with the 256px
 * sidebar OPEN — content ≈ min(viewport − 256 − 48, 1280): lg 1024 → 720, xl 1280 → 976. A sticky
 * panel in a wrapped/stacked layout slides over the content below it, so nothing applies below.
 * - The PANEL (the flex item) is what sticks: the row is as tall as the tallest panel, so it has
 *   room to travel. A sticky card INSIDE an items-start panel is inert (the panel is only as tall
 *   as the card).
 * - `scroll` (default) caps the panel at the viewport and scrolls it inside, so a long output can
 *   always be reached. Pass `scroll={false}` for a three.js canvas (internal scroll fights
 *   drag-to-rotate) — only when the panel is short enough to fit a desktop viewport anyway.
 * Literal strings (not built from the breakpoint) so Tailwind's scanner sees every class.
 */
const STICKY_STYLES: Record<'lg' | 'xl', { base: string; scroll: string }> = {
  lg: {
    base: 'lg:sticky lg:top-6 lg:self-start',
    scroll: 'lg:max-h-[calc(100vh-3rem-var(--cookie-banner-h,0px)-var(--ad-anchor-bottom,0px))] lg:overflow-y-auto',
  },
  xl: {
    base: 'xl:sticky xl:top-6 xl:self-start',
    scroll: 'xl:max-h-[calc(100vh-3rem-var(--cookie-banner-h,0px)-var(--ad-anchor-bottom,0px))] xl:overflow-y-auto',
  },
};

interface ToolPanelProps {
  children: ReactNode;
  size?: PanelSize;
  className?: string;
  /** Stick from this breakpoint up — only one where this panel is guaranteed BESIDE the others. */
  stickyFrom?: 'lg' | 'xl';
  /** With `stickyFrom`: cap at the viewport height and scroll inside (default true). */
  stickyScroll?: boolean;
}

export function ToolPanel({ children, size = 'md', className = '', stickyFrom, stickyScroll = true }: ToolPanelProps) {
  const sticky = stickyFrom
    ? `${STICKY_STYLES[stickyFrom].base}${stickyScroll ? ` ${STICKY_STYLES[stickyFrom].scroll}` : ''}`
    : '';
  return (
    <div className={`${PANEL_STYLES[size]} min-w-0 ${sticky} ${className}`}>
      {children}
    </div>
  );
}
